import {gql} from "@apollo/client";
import {mutate, query} from "../../lib/apollo";

const CAP_FIELDS = `
    hourlyCap { isOn value }
    dailyCap { isOn value }
    monthlyCap { isOn value }
    mondayCap { isOn value }
    tuesdayCap { isOn value }
    wednesdayCap { isOn value }
    thursdayCap { isOn value }
    fridayCap { isOn value }
    saturdayCap { isOn value }
    sundayCap { isOn value }
    mondayHourlyCap { isOn value }
    tuesdayHourlyCap { isOn value }
    wednesdayHourlyCap { isOn value }
    thursdayHourlyCap { isOn value }
    fridayHourlyCap { isOn value }
    saturdayHourlyCap { isOn value }
    sundayHourlyCap { isOn value }
    mondayPacingCap { isOn value }
    tuesdayPacingCap { isOn value }
    wednesdayPacingCap { isOn value }
    thursdayPacingCap { isOn value }
    fridayPacingCap { isOn value }
    saturdayPacingCap { isOn value }
    sundayPacingCap { isOn value }
`;

const GROUP_FIELDS = `
    id
    name
    notes
    emails
    partnerId
    pacingProvider
    isActive
    isTriggered
    isPaused
    hasCaps
    ${CAP_FIELDS}
`;

export async function fetchGroupCaps(search: string) {
    const q = gql`
        query q($search: String) {
            groupCaps(search: $search) {
                ${GROUP_FIELDS}
            }
        }
    `;
    const res = await query(q, {search: search || null});
    return res?.groupCaps || [];
}

export async function fetchGroupCapCampaigns(ids: number[]) {
    if (!ids.length) {
        return [];
    }
    const q = gql`
        query q($ids: [Int!]!) {
            groupCapCampaigns(ids: $ids) {
                groupCapsId
                campaigns {
                    hpCampaignId
                    hpCompany
                    hpEventId
                    appId
                    schoolId
                    schoolName
                    brokerId
                    brokerName
                    timeZone
                    dailyCap
                    monthlyCap
                    hourly
                    daily
                    yesterday
                    monthly
                    appRate30d
                    total30d
                }
            }
        }
    `;
    const res = await query(q, {ids});
    return res?.groupCapCampaigns || [];
}

export function emptyCap() {
    return {isOn: false, value: null as number | null};
}

/** Strip Apollo __typename etc. before sending caps as input. */
function capInput(cap: any) {
    if (!cap) {
        return emptyCap();
    }
    return {
        isOn: !!cap.isOn,
        value: cap.value == null || cap.value === "" ? null : Number(cap.value),
    };
}

const CAP_INPUT_KEYS = [
    "hourlyCap", "dailyCap", "monthlyCap",
    "mondayCap", "tuesdayCap", "wednesdayCap", "thursdayCap", "fridayCap", "saturdayCap", "sundayCap",
    "mondayHourlyCap", "tuesdayHourlyCap", "wednesdayHourlyCap", "thursdayHourlyCap", "fridayHourlyCap", "saturdayHourlyCap", "sundayHourlyCap",
    "mondayPacingCap", "tuesdayPacingCap", "wednesdayPacingCap", "thursdayPacingCap", "fridayPacingCap", "saturdayPacingCap", "sundayPacingCap",
] as const;

export async function upsertGroupCap(input: any) {
    const clean: any = {
        name: input.name,
        notes: input.notes,
        emails: input.emails,
        pacingProvider: input.pacingProvider || "",
        hpCampaignIds: input.hpCampaignIds,
    };
    if (input.id != null) {
        clean.id = input.id;
        clean.partnerId = Number(input.partnerId || 0);
    }
    for (const key of CAP_INPUT_KEYS) {
        clean[key] = capInput(input[key]);
    }

    const m = gql`
        mutation m($input: GroupCapUpsertInput!) {
            groupCapUpsert(input: $input)
        }
    `;
    const res = await mutate(m, {input: clean});
    return res?.groupCapUpsert;
}

export async function setGroupCapActive(id: number, active: boolean) {
    const m = gql`
        mutation m($id: Int!, $active: Boolean!) {
            groupCapSetActive(id: $id, active: $active)
        }
    `;
    const res = await mutate(m, {id, active});
    return !!res?.groupCapSetActive;
}

export async function setGroupCapPaused(id: number, paused: boolean) {
    const m = gql`
        mutation m($id: Int!, $paused: Boolean!) {
            groupCapSetPaused(id: $id, paused: $paused)
        }
    `;
    const res = await mutate(m, {id, paused});
    return !!res?.groupCapSetPaused;
}

export async function deleteGroupCap(id: number) {
    const m = gql`
        mutation m($id: Int!) {
            groupCapDelete(id: $id)
        }
    `;
    const res = await mutate(m, {id});
    return !!res?.groupCapDelete;
}

export function applyCampaigns(row: any, campaigns: any[]) {
    const totals = {
        dailyCap: 0,
        monthlyCap: 0,
        hourly: 0,
        daily: 0,
        monthly: 0,
        appRate30d: 0,
        total30d: 0,
        yesterday: 0,
    };
    const list = campaigns || [];
    list.forEach((c: any) => {
        totals.dailyCap += c.dailyCap || 0;
        totals.monthlyCap += c.monthlyCap || 0;
        totals.hourly += c.hourly || 0;
        totals.daily += c.daily || 0;
        totals.yesterday += c.yesterday || 0;
        totals.monthly += c.monthly || 0;
        totals.total30d += c.total30d || 0;
        totals.appRate30d += (c.appRate30d || 0) * (c.total30d || 0);
    });
    if (totals.total30d) {
        totals.appRate30d = totals.appRate30d / totals.total30d;
    }
    return {...row, campaigns: list, totals};
}
